import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import Breadcrumb from '../components/Breadcrumb';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Plus, Edit, Trash2, Eye, Loader2, FileText, Upload, X, Image as ImageIcon, AlertTriangle, Check } from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';
import { useBlogSeoScore, calculateBlogSeoScore } from '../hooks/useSeoScore';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogManagementPage = () => {
  const { t } = useTranslation();
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchPosts();
  }, [user, authLoading]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blog/admin/posts`, { headers });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/admin/posts/${editingPost.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/blog/admin/posts`, formData, { headers });
      }
      fetchPosts();
      setShowModal(false);
      setEditingPost(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Yazı kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/blog/admin/posts/${postId}`, { headers });
      fetchPosts();
    } catch (error) {
      alert('Yazı silinemedi');
    }
  };

  const togglePublished = async (post) => {
    try {
      await axios.put(`${API}/blog/admin/posts/${post.id}`,
        { published: !post.published },
        { headers }
      );
      fetchPosts();
    } catch (error) {
      alert('Yazı güncellenemedi');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-800">
      {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: 'Yönetim Paneli', href: '/admin' },
        { label: t('blog.blogManagement'), href: null }
      ]} />

      <div className="container mx-auto px-4 py-8">

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">{t('blog.blogManagement')}</h1>
            <p className="text-zinc-400 mt-1">{t('blog.manageSeoFriendly')}</p>
          </div>
          <Button
            onClick={() => { setEditingPost(null); setShowModal(true); }}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('blog.newPost')}
          </Button>
        </div>

        {/* Posts List */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800">
          {posts.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-400 mb-4">{t('blog.noPosts')}</p>
              <Button
                onClick={() => { setEditingPost(null); setShowModal(true); }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {t('blog.createFirstPost')}
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {posts.map((post) => (
                <div key={post.id} className="p-4 flex items-center justify-between hover:bg-zinc-800/50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-white font-medium truncate">{post.title}</h3>
                      <Badge className={post.published
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'bg-zinc-700 text-zinc-400'
                      }>
                        {post.published ? t('blog.published') : t('blog.draft')}
                      </Badge>
                      {post.category && (
                        <Badge className="bg-blue-500/20 text-blue-400">
                          {post.category}
                        </Badge>
                      )}
                      {/* SEO Score Badge */}
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        post.seo_score >= 70 ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                        post.seo_score >= 40 ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                        'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`} title={`SEO: ${post.seo_score || 0}`}>
                        {post.seo_score || 0}
                      </div>
                    </div>
                    <p className="text-zinc-500 text-sm truncate">{post.excerpt}</p>
                    <p className="text-zinc-600 text-xs mt-1">{formatDate(post.created_at)}</p>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <Switch
                      checked={post.published}
                      onCheckedChange={() => togglePublished(post)}
                      title={post.published ? t('blog.draft') : t('blog.publishNow')}
                    />
                    <a
                      href={`/blog/${post.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-blue-400 hover:bg-zinc-700 rounded-lg transition-colors"
                      title={t('common.view')}
                    >
                      <Eye className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => { setEditingPost(post); setShowModal(true); }}
                      className="p-2 text-yellow-400 hover:bg-zinc-700 rounded-lg transition-colors"
                      title={t('common.edit')}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="p-2 text-red-400 hover:bg-zinc-700 rounded-lg transition-colors" title={t('common.delete')}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">{t('blog.deletePost')}</AlertDialogTitle>
                          <AlertDialogDescription className="text-zinc-400">
                            {t('blog.confirmDeletePost')}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-zinc-800 text-zinc-300 border-zinc-700">{t('common.cancel')}</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            onClick={() => handleDelete(post.id)}
                          >
                            {t('common.delete')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Post Editor Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPost ? t('blog.editPost') : t('blog.newPost')}</DialogTitle>
          </DialogHeader>
          <PostForm
            post={editingPost}
            onSave={handleSave}
            onCancel={() => setShowModal(false)}
            saving={saving}
            token={token}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Post Form Component
const PostForm = ({ post, onSave, onCancel, saving, token }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    featured_image: post?.featured_image || '',
    featured_image_alt: post?.featured_image_alt || '',
    category: post?.category || '',
    tags: post?.tags?.join(', ') || '',
    published: post?.published || false,
    seo: {
      title: post?.seo?.title || '',
      description: post?.seo?.description || '',
      keywords: post?.seo?.keywords || '',
      og_image: post?.seo?.og_image || '',
      robots: post?.seo?.robots || 'index, follow',
      canonical_url: post?.seo?.canonical_url || ''
    }
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [seoWarning, setSeoWarning] = useState(null);
  const [showSeoAlert, setShowSeoAlert] = useState(false);
  const [expandedTip, setExpandedTip] = useState(null);

  const liveScore = useBlogSeoScore(formData);

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9ğüşıöçİĞÜŞÖÇ]+/gi, '-').replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({ ...formData, title, slug: formData.slug || generateSlug(title) });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) { alert('Geçersiz dosya türü. PNG, JPG veya WebP yükleyin.'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('Dosya çok büyük. Maksimum 5MB.'); return; }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      const imageUrl = response.data.url.startsWith('http') ? response.data.url : `${BACKEND_URL}${response.data.url}`;
      setFormData({ ...formData, featured_image: imageUrl });
    } catch (error) {
      alert('Görsel yüklenemedi');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = () => setFormData({ ...formData, featured_image: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    const tagsArray = formData.tags.split(',').map(t => t.trim()).filter(Boolean);
    const submitData = { ...formData, tags: tagsArray };
    const { score, suggestions } = calculateBlogSeoScore(submitData);
    if (score < 50 && !showSeoAlert) { setSeoWarning({ score, suggestions }); setShowSeoAlert(true); return; }
    setShowSeoAlert(false); setSeoWarning(null); onSave(submitData);
  };

  const proceedWithLowScore = () => {
    setShowSeoAlert(false);
    const tagsArray = formData.tags.split(',').map(t => t.trim()).filter(Boolean);
    onSave({ ...formData, tags: tagsArray });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Live SEO Score Badge */}
      <div className={`rounded-lg border ${liveScore.getScoreBorderColor(liveScore.score)} bg-zinc-800/50`}>
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-base ${liveScore.getScoreColor(liveScore.score)}`}>
              {liveScore.score}
            </div>
            <div>
              <p className="text-white font-medium text-sm">{t('seo.seoScore')}</p>
              <p className="text-zinc-500 text-xs">
                {liveScore.score >= 70 ? t('seo.greatReadyToPublish') :
                 liveScore.score >= 50 ? t('seo.goodButCanImprove') :
                 t('seo.needsImprovement')}
              </p>
            </div>
          </div>
          {(liveScore.suggestions.length > 0 || liveScore.completed.length > 0) && (
            <button type="button" onClick={() => setExpandedTip(expandedTip ? null : 'main')} className="text-right hover:bg-zinc-700/50 p-2 rounded-lg transition-colors max-w-[180px]">
              <p className="text-zinc-400 text-xs">{liveScore.completed.length} {t('seo.completed').toLowerCase()}, {liveScore.suggestions.length} {t('seo.toImprove')}</p>
              <p className="text-emerald-400 text-xs truncate flex items-center gap-1">
                {liveScore.suggestions.length > 0 ? liveScore.suggestions[0]?.title : t('seo.completed')}
                <svg className={`w-3 h-3 transition-transform ${expandedTip ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </p>
            </button>
          )}
        </div>

        {expandedTip && (
          <div className="border-t border-zinc-700 p-3 space-y-3">
            {liveScore.completed.length > 0 && (
              <div className="space-y-2">
                <p className="text-emerald-400 text-xs font-medium flex items-center gap-1"><Check className="w-3 h-3" /> {t('seo.completed')} ({liveScore.completed.length})</p>
                <div className="grid grid-cols-2 gap-2">
                  {liveScore.completed.map((item, idx) => (
                    <div key={item.key || idx} className="flex items-center gap-2 text-sm">
                      <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-white" /></div>
                      <span className="text-zinc-400 text-xs">{item.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {liveScore.suggestions.length > 0 && (
              <div className="space-y-2">
                <p className="text-red-400 text-xs font-medium flex items-center gap-1"><X className="w-3 h-3" /> {t('seo.needsImprovement')} ({liveScore.suggestions.length})</p>
                {liveScore.suggestions.slice(0, 4).map((suggestion, idx) => (
                  <div key={suggestion.key || idx} className="bg-zinc-900/50 rounded-lg overflow-hidden">
                    <button type="button" onClick={() => setExpandedTip(expandedTip === suggestion.key ? 'main' : suggestion.key)} className="w-full flex items-center justify-between p-2 hover:bg-zinc-700/30 transition-colors text-left">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0"><X className="w-3 h-3 text-white" /></div>
                        <span className="text-zinc-300 text-sm">{suggestion.title}</span>
                      </div>
                    </button>
                    {expandedTip === suggestion.key && suggestion.tips && (
                      <div className="px-3 pb-3 space-y-1">
                        {suggestion.tips.map((tip, tipIdx) => (
                          <div key={tipIdx} className="flex items-start gap-2"><span className="text-emerald-400 text-xs mt-0.5">•</span><span className="text-zinc-400 text-xs">{tip}</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-zinc-300">{t('blog.postTitle')}</Label>
          <Input value={formData.title} onChange={handleTitleChange} className="bg-zinc-800 border-zinc-700 text-white" placeholder={t('blog.postTitle')} required />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">{t('blog.slug')}</Label>
          <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" placeholder="yazi-url-slug" required />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-300">{t('blog.excerpt')}</Label>
        <Textarea value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" rows={2} placeholder={t('blog.excerptPlaceholder')} required />
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-300">{t('blog.postContent')}</Label>
        <RichTextEditor value={formData.content} onChange={(content) => setFormData({ ...formData, content })} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-3">
          <Label className="text-zinc-300">{t('blog.featuredImage')}</Label>
          {formData.featured_image ? (
            <div className="relative bg-zinc-800 rounded-lg p-3">
              <img src={formData.featured_image} alt={t('blog.featuredImage')} className="max-h-40 mx-auto rounded" />
              <button type="button" onClick={removeImage} className="absolute top-2 right-2 bg-red-500 rounded-full p-1 hover:bg-red-600 transition-colors"><X className="w-4 h-4 text-white" /></button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-emerald-500 transition-colors bg-zinc-800/50">
              <Upload className="w-8 h-8 text-zinc-500 mb-2" />
              <span className="text-zinc-400 text-sm">Görsel yüklemek için tıklayın</span>
              <span className="text-zinc-600 text-xs mt-1">PNG, JPG, WebP (maks 5MB)</span>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={handleImageUpload} className="hidden" disabled={uploading} />
            </label>
          )}
          {uploading && <p className="text-emerald-400 text-sm text-center">{t('common.loading')}</p>}
          <div className="space-y-1">
            <Label className="text-zinc-500 text-xs">Veya URL yapıştırın:</Label>
            <Input value={formData.featured_image} onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white text-sm" placeholder="https://..." />
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">{t('blog.featuredImageAlt')}</Label>
          <Input value={formData.featured_image_alt} onChange={(e) => setFormData({ ...formData, featured_image_alt: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" placeholder={t('seo.describeImage')} />
          <p className="text-zinc-600 text-xs">{t('seo.helpsWithSeo')}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-zinc-300">{t('blog.category')}</Label>
          <Input value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" placeholder="Haberler, İpuçları, vb." />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">{t('blog.tagsComma')}</Label>
          <Input value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" placeholder="seo, ipuçları, rehber" />
        </div>
      </div>

      {/* SEO Settings */}
      <div className="border-t border-zinc-800 pt-6">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          {t('seo.seoSettings')}
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">{t('seo.metaTitle')}</Label>
              <Input value={formData.seo.title} onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, title: e.target.value } })} className="bg-zinc-800 border-zinc-700 text-white" placeholder={t('seo.leaveEmptyTitle')} />
              <p className="text-zinc-600 text-xs">{formData.seo.title.length}/60 {t('seo.charsCount')}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">{t('seo.robotsMeta')}</Label>
              <select value={formData.seo.robots} onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, robots: e.target.value } })} className="w-full bg-zinc-800 border border-zinc-700 text-zinc-300 rounded-md px-3 py-2">
                <option value="index, follow">{t('seo.indexFollow')}</option>
                <option value="index, nofollow">{t('seo.indexNoFollow')}</option>
                <option value="noindex, follow">{t('seo.noIndexFollow')}</option>
                <option value="noindex, nofollow">{t('seo.noIndexNoFollow')}</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-400">{t('seo.metaDescription')}</Label>
            <Textarea value={formData.seo.description} onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, description: e.target.value } })} className="bg-zinc-800 border-zinc-700 text-white" rows={2} placeholder={t('seo.leaveEmptyExcerpt')} />
            <p className="text-zinc-600 text-xs">{formData.seo.description.length}/160 {t('seo.charsCount')}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">{t('seo.keywords')}</Label>
              <Input value={formData.seo.keywords} onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, keywords: e.target.value } })} className="bg-zinc-800 border-zinc-700 text-white" placeholder="anahtar1, anahtar2" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">{t('seo.ogImage')}</Label>
              <Input value={formData.seo.og_image} onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, og_image: e.target.value } })} className="bg-zinc-800 border-zinc-700 text-white" placeholder={t('seo.leaveEmptyFeatured')} />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-400">{t('seo.canonicalUrl')}</Label>
            <Input value={formData.seo.canonical_url} onChange={(e) => setFormData({ ...formData, seo: { ...formData.seo, canonical_url: e.target.value } })} className="bg-zinc-800 border-zinc-700 text-white" placeholder={t('seo.canonicalUrlLeaveEmpty')} />
          </div>
        </div>
      </div>

      {/* SEO Score Warning Alert */}
      {showSeoAlert && seoWarning && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-5 mb-4">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">{t('seo.lowSeoWarning')}</h3>
              <p className="text-zinc-400 text-sm mt-1">
                SEO puanı: <span className="text-yellow-400 font-bold">{seoWarning.score}/100</span>
              </p>
            </div>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-3 mb-4">
            <p className="text-zinc-300 text-sm font-medium mb-2">{t('seo.suggestionsToImprove')}</p>
            <ul className="space-y-1">
              {seoWarning.suggestions.slice(0, 4).map((suggestion, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-zinc-400"><span className="text-yellow-400">•</span>{suggestion.title}</li>
              ))}
            </ul>
          </div>
          <div className="flex gap-3">
            <Button type="button" onClick={() => setShowSeoAlert(false)} size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700">{t('seo.improveSeo')}</Button>
            <Button type="button" onClick={proceedWithLowScore} size="sm" variant="outline" className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              {formData.published ? t('seo.publishAnyway') : t('seo.saveAnyway')}
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
        <div className="flex items-center gap-3">
          <Switch checked={formData.published} onCheckedChange={(checked) => setFormData({ ...formData, published: checked })} />
          <span className="text-zinc-400">{t('blog.publishNow')}</span>
        </div>
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onCancel} className="border-zinc-700 text-zinc-300">{t('common.cancel')}</Button>
          <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? t('common.saving') : (formData.published ? t('blog.publishNow') : t('blog.saveDraft'))}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default BlogManagementPage;
